import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import Sidebar from "@/components/Sidebar";
import MobileHeader from "@/components/MobileHeader";
import VideoJobIndicator from "@/components/VideoJobIndicator";
import BugReportButton from "@/components/BugReportButton";
import TrialBanner from "@/components/TrialBanner";
import TrialEndedModal from "@/components/TrialEndedModal";
import { useAuth } from "@/context/AuthContext";
import { VideoJobProvider } from "@/context/VideoJobContext";
import { FeatureFlagsProvider, useFeatureFlags } from "@/context/FeatureFlagsContext";
import { useTheme } from "@/context/ThemeContext";
import { Loader2 } from "lucide-react";

// If the admin hides the user's currently selected theme, snap back to light.
// This lives inside FeatureFlagsProvider so flags are available.
function ThemeFlagGuard() {
  const { theme, setTheme, themes } = useTheme();
  const { isHidden, loaded } = useFeatureFlags();
  useEffect(() => {
    if (!loaded) return;
    const current = themes.find((t) => t.id === theme);
    if (current && isHidden(current.flag) && theme !== "light") {
      setTheme("light");
    }
  }, [theme, themes, isHidden, loaded, setTheme]);
  return null;
}

export default function AppLayout() {
  const location = useLocation();
  // Every page fills the full remaining viewport width after the fixed
  // sidebar. Individual pages can still cap readable text blocks inside
  // via their own inner max-width utilities — the outer chrome never
  // caps or leaves a white column on wide monitors.
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }
  if (!user) {
    // Preserve the deep-link destination (e.g. /app/pair-desktop?device_id=…)
    // so that after signing in the user lands back where they came from.
    const back = `${location.pathname}${location.search || ""}`;
    const next = back === "/app" ? "" : `?next=${encodeURIComponent(back)}`;
    return <Navigate to={`/auth${next}`} replace />;
  }

  return (
    <FeatureFlagsProvider>
      <ThemeFlagGuard />
      <VideoJobProvider>
        {/*
          Full-viewport shell:
          - min-h-[100dvh] anchors the outer container to the visual viewport
            (dvh accounts for mobile browser chrome collapsing → no phantom
            gap under the fold).
          - flex-col + flex-1 on the middle row lets the sidebar + main
            row stretch to fill the height, so the app looks like a real
            dashboard, not a floating card on white background.
        */}
        <div
          className="min-h-[100dvh] bg-[#FAFAFA] flex flex-col overflow-x-hidden"
          data-chat-focus={location.pathname.startsWith("/app/chat") ? "1" : undefined}
        >
          <TrialBanner />
          <MobileHeader />
          {/*
            grid-cols-[260px_1fr] on desktop → sidebar keeps its 260px
            rail, main takes the rest via 1fr. min-w-0 on the grid child
            is CRITICAL — without it the 1fr column refuses to shrink
            below its intrinsic content width and creates horizontal
            overflow (see: CSS grid min-content sizing bug). The `flex-1`
            here + `min-h-0` on the grid + `h-full` inheritance on main
            makes chat-style pages able to fill available height.
          */}
          <div className="flex-1 grid grid-cols-1 md:grid-cols-[260px_minmax(0,1fr)] min-w-0 min-h-0 app-grid">
            <Sidebar />
            {/*
              px-6 py-6 for internal breathing room on normal pages. Pages
              that want an edge-to-edge canvas (Chat) opt in by wrapping
              themselves in a <div className="page-fullbleed"> which is
              defined in index.css to zero the padding.
            */}
            <main className="px-4 sm:px-6 lg:px-8 py-6 w-full min-w-0 flex flex-col min-h-0">
              <Outlet />
            </main>
          </div>
          <VideoJobIndicator />
          <BugReportButton />
          <TrialEndedModal />
        </div>
      </VideoJobProvider>
    </FeatureFlagsProvider>
  );
}
